import { api } from "core";
import { IPaginatedTransaction, ITransaction } from "./types";

export async function getBalanceUser() {
    return api.get("payment/getbalance-user");
}


/** Adapter */
/** Adapter: normalize backend fields */
function adaptTransaction(raw: any): ITransaction {
    return {
        id: Number(raw.id),
        transactionId: String(raw.transaction_id ?? ""),
        paymentType: raw.payment_type ?? "",
        amount: Number(raw.amount ?? 0),
        paymentDate: raw.payment_date ?? "",
        paymentGateway: raw.payment_gateway ?? "",
        status: raw.status ?? "",
        message: raw.message ?? "",
        credits: Number(raw.credits ?? 0),
        cStatus: raw.c_status ?? undefined,
    };
}

/** Fetch paginated transactions (0-based paginator param) */
export async function getTransactions(page = 1): Promise<IPaginatedTransaction<ITransaction>> {
    const { data } = await api.get("all-receipts", {
        params: { paginator: Number(page) - 1 },
    });

    const rawDetails = data?.receipt_details ?? {};
    const items = Object.values(rawDetails).map(adaptTransaction);

    return {
        data: items,
        total: Number(data?.receipt_count ?? items.length),
        page,
        limit: 10,
    };
}

/** Fetch ALL transactions for KPI calculations (fetches all pages) */
export async function getAllTransactionsForKPI(): Promise<{
    totalCount: number;
    totalAmount: number;
    totalCredits: number;
    successRate: number;
}> {
    try {
        // First, fetch page 1 to get the total count
        const firstPage = await getTransactions(1);
        const totalCount = firstPage.total;
        const totalPages = Math.ceil(totalCount / 10);

        // Collect all transactions from all pages
        let allTransactions: ITransaction[] = [...firstPage.data];

        // Fetch remaining pages if there are more than 1 page
        if (totalPages > 1) {
            const pagePromises = [];
            for (let page = 2; page <= totalPages; page++) {
                pagePromises.push(getTransactions(page));
            }

            const results = await Promise.all(pagePromises);
            results.forEach(result => {
                allTransactions = allTransactions.concat(result.data);
            });
        }

        // Calculate aggregates from all transactions
        const totalAmount = allTransactions.reduce((sum, t) => sum + t.amount, 0);
        const totalCredits = allTransactions.reduce((sum, t) => sum + t.credits, 0);
        const successCount = allTransactions.filter(t => t.status === 'SUCCESS').length;
        const successRate = allTransactions.length > 0
            ? Math.round((successCount / allTransactions.length) * 100)
            : 0;

        return {
            totalCount,
            totalAmount,
            totalCredits,
            successRate,
        };
    } catch (error) {
        console.error('Error fetching KPI data:', error);
        return {
            totalCount: 0,
            totalAmount: 0,
            totalCredits: 0,
            successRate: 0,
        };
    }
}

/** Download invoice (POST body with transactionId) */
export async function downloadInvoice(transactionId: string): Promise<string> {
    const { data } = await api.post("payment/get-stripe-invoice", { transactionId });
    return data?.invoice_pdf ?? "";
}